import React, { useEffect, useState } from "react";
import { styled } from '@mui/material/styles';
import { Box, Container, Grid, Typography, Pagination } from '@mui/material';
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar/Navbar";
import Footer from "../components/Home/Footer";
import BrowserNotSupported from "./BrowserNotSupported";
import ScoreCard from "../components/IELTS/ScoreCard";
import TestCard from "../components/IELTS/TestCard";
import SectionCard from "../components/IELTS/SectionCard";
import SectionalTestCard from "../components/IELTS/SectionalTestCard";
import TestSection from "../components/IELTS/TestSection";
import { 
  CalculatorIcon, 
  TargetIcon, 
  CalendarIcon,
  ListeningIcon,
  ReadingIcon,
  WritingIcon,
  SpeakingIcon
} from "../components/IELTS/icons";
import { 
  testFilters, 
  sections, 
} from "../components/IELTS/mockData";
import { Base_URL } from "../Client/apiURL";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, TextField, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { themeColors } from '../components/IELTS/styles/theme';

const isNotChrome = () => {
  const userAgent = navigator.userAgent;
  return !/Chrome/.test(userAgent) || /OPR|Edge/.test(userAgent);
};

const DashboardContainer = styled(Container)(({ theme }) => ({
  paddingTop: '6rem',
  paddingBottom: '2rem',
  [theme.breakpoints.down('md')]: {
    paddingTop: '2rem',
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
  },
  [theme.breakpoints.down('sm')]: {
    paddingLeft: '1rem',
    paddingRight: '1rem',
  },
}));

const TestListContainer = styled(Box)({
  minHeight: '500px',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flex: 1,
});

const IELTSDashboard = () => {
  const navigate = useNavigate();
  const isLaptopTwo = useMediaQuery("(max-width:1000px)");
  const isMobile = useMediaQuery("(max-width:600px)");
  const [open, setOpen] = React.useState(false);
  const [showFooter, setShowFooter] = React.useState(false);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedKind, setSelectedKind] = useState(testFilters[0].value);
  const [selectedSection, setSelectedSection] = useState(sections[0].title);
  const [testKinds, setTestKinds] = useState([]);
  const [sectionCounts, setSectionCounts] = useState({
    Listening: 0,
    Reading: 0,
    Writing: 0,
    Speaking: 0
  });
  // IELTS Profile data state
  const [profileData, setProfileData] = useState({
    averageScore: "- / 9",
    targetScore: "- / 9", 
    examIn: "- Days",
    loading: true,
    error: null
  });
  // Pagination states
  const [fullTestPage, setFullTestPage] = useState(1);
  const [sectionalTestPage, setSectionalTestPage] = useState(1);
  const fullTestsPerPage = 6;
  const sectionalTestsPerPage = 5;
  
  const [loadingTestId, setLoadingTestId] = useState(null);
  
  // Change score/date modal states
  const [showTargetScoreModal, setShowTargetScoreModal] = useState(false);
  const [showExamDateModal, setShowExamDateModal] = useState(false);
  const [newTargetScore, setNewTargetScore] = useState('');
  const [newExamDate, setNewExamDate] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Custom styles for dropdown
  const selectStyles = {
    '& .MuiSelect-select': {
      backgroundColor: '#FFFFFF',
      border: '1px solid #E2E8F0',
      borderRadius: '10px',
      padding: '6px 32px 6px 16px',
      fontSize: '13px',
      fontWeight: 700,
      fontFamily: "'Poppins', 'Inter', sans-serif",
      '&:focus': {
        backgroundColor: '#FFFFFF',
        borderColor: themeColors.primary,
      },
      '&:hover': {
        borderColor: '#CBD5E0',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiSvgIcon-root': {
      color: themeColors.primary,
    },
  };

  // Custom pagination styles
  const paginationStyles = {
    '& .MuiPaginationItem-root': {
      color: '#4A5568',
      fontWeight: 700,
      fontSize: '13px',
      borderRadius: '8px',
      fontFamily: "'Poppins', 'Inter', sans-serif",
      '&.Mui-selected': {
        backgroundColor: themeColors.primary,
        color: 'white',
        '&:hover': {
          backgroundColor: themeColors.primaryHover,
        },
      },
      '&:hover': {
        backgroundColor: '#F7FAFC',
      },
    },
    '& .MuiPaginationItem-ellipsis': {
      color: '#A0AEC0',
    },
    '& .MuiPaginationItem-previousNext': {
      backgroundColor: '#FFFFFF',
      border: '1px solid #E2E8F0',
      '&:hover': {
        backgroundColor: '#F7FAFC',
      },
      '&.Mui-disabled': {
        backgroundColor: '#EDF2F7',
        borderColor: '#E2E8F0',
      },
    },
  };

  // Track selected IELTS test type (general or academic)
  const [selectedTestType, setSelectedTestType] = useState(() => {
    const stored = localStorage.getItem('ielts-type');
    return stored ? stored.toLowerCase() : 'academic';
  });

  // Update section counts when tests are loaded or selectedTestType changes
  // Count ALL tests (with or without groupId) for each category
  useEffect(() => {
    if (tests.length > 0) {
      const counts = {
        Listening: 0,
        Reading: 0,
        Writing: 0,
        Speaking: 0
      };
      
      tests.forEach(test => {
        if (test.category_name) {
          // Filter by selectedTestType (academic/general)
          const matchesType = test.test_type ? test.test_type.toLowerCase() === selectedTestType : true;
          
          if (matchesType) {
            // Count all tests including those with groupId
            const category = test.category_name.toLowerCase();
            if (category.includes('listening')) counts.Listening++;
            else if (category.includes('reading')) counts.Reading++;
            else if (category.includes('writing')) counts.Writing++;
            else if (category.includes('speaking')) counts.Speaking++;
          }
        }
      });
      
      setSectionCounts(counts);
    }
  }, [tests, selectedTestType]);

  // Fetch IELTS profile data
  const fetchProfileData = async () => {
    try {
      setProfileData(prev => ({ ...prev, loading: true, error: null }));
      
      const response = await fetch(`${Base_URL}/app/users/get-ielts-profile`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });
      
      const data = await response.json();
      
      if (data.responseCode === 200) {
        const profile = data.response;
        setProfileData({
          averageScore: profile.average_score ? `${profile.average_score} / 9` : "- / 9",
          targetScore: profile.ielts_target_score ? `${profile.ielts_target_score} / 9` : "- / 9",
          examIn: profile.days_until_exam ? `${String(profile.days_until_exam).padStart(2, '0')} Days` : "- Days",
          loading: false,
          error: null
        });
      } else {
        throw new Error(data.message || "Failed to fetch profile data");
      }
    } catch (error) {
      console.error("Error fetching IELTS profile:", error);
      setProfileData(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  };
  
  useEffect(() => {
    setOpen(isNotChrome());
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const portalId = userData?.PortalId || 0;
    setShowFooter(portalId === 1);
    
    // Set exam type to IELTS in localStorage to maintain state
    localStorage.setItem("exam-type", "ielts");
    
    // Set default IELTS type if not already set
    if (!localStorage.getItem("ielts-type")) {
      localStorage.setItem("ielts-type", "academic");
    }
    
    // Dispatch a custom event to notify other components about the exam type
    const event = new CustomEvent("examTypeChanged", { detail: { examType: "ielts" } });
    window.dispatchEvent(event);

    // Fetch IELTS tests from API
    const fetchTests = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${Base_URL}/app/users/ielts-tests`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        let testsArr = [];
        if (Array.isArray(data)) {
          testsArr = data;
        } else if (Array.isArray(data.response)) {
          testsArr = data.response;
        }
        setTests(testsArr);
        // Extract unique test_kind values
        const kinds = Array.from(new Set(testsArr.map(t => t.test_kind).filter(Boolean)));
        setTestKinds(kinds);
        
      } catch (err) {
        setError("Failed to fetch IELTS tests");
        setTests([]);
        setTestKinds([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTests();
    fetchProfileData();

    // Listen for IELTS type changes from the toggle component
    const handleIeltsTypeChange = (e) => {
      const { ieltsType } = e.detail || {};
      if (ieltsType) {
        setSelectedTestType(ieltsType.toLowerCase());
      }
    };
    window.addEventListener('ieltsTypeChanged', handleIeltsTypeChange);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('ieltsTypeChanged', handleIeltsTypeChange);
    };
  }, []);

  const handleChangeTargetScore = () => {
    setShowTargetScoreModal(true);
  };

  const handleChangeExamDate = () => {
    setShowExamDateModal(true);
  };

  const updateTargetScore = async () => {
    if (!newTargetScore) return;
    
    try {
      setIsUpdating(true);
      
      const response = await fetch(`${Base_URL}/app/users/update-ielts-profile`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ielts_target_score: parseFloat(newTargetScore)
        })
      });
      
      const data = await response.json();
      
      if (data.responseCode === 200) {
        // Refresh profile data after successful update
        await fetchProfileData();
        setShowTargetScoreModal(false);
        setNewTargetScore('');
      } else {
        console.error("Failed to update target score:", data.message);
        alert("Failed to update target score. Please try again.");
      }
    } catch (error) {
      console.error("Error updating target score:", error);
      alert("Error updating target score. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  const updateExamDate = async () => {
    if (!newExamDate) return;
    
    try {
      setIsUpdating(true);
      
      const response = await fetch(`${Base_URL}/app/users/update-ielts-profile`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ielts_exam_date: newExamDate
        })
      });
      
      const data = await response.json();
      
      if (data.responseCode === 200) {
        // Refresh profile data after successful update
        await fetchProfileData();
        setShowExamDateModal(false);
        setNewExamDate('');
      } else {
        console.error("Failed to update exam date:", data.message);
        alert("Failed to update exam date. Please try again.");
      }
    } catch (error) {
      console.error("Error updating exam date:", error);
      alert("Error updating exam date. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  /** Build a resume-context-shaped payload from catalog rows + list summary (no resume-context API). */
  const buildResumePrefetchFromTestsList = (catalogSorted, summary, attemptId, groupId) => {
    const isSpeakingOptional = !!summary?.is_speaking_optional;
    const sectionOrder = isSpeakingOptional
      ? ['Listening', 'Reading', 'Writing']
      : ['Listening', 'Reading', 'Writing', 'Speaking'];
    const completedCount = Math.min(
      Math.max(0, Number(summary?.completed_sections_count || 0)),
      sectionOrder.length
    );
    const tests = sectionOrder
      .map((categoryName, idx) => {
        const row = catalogSorted.find((t) => t.category_name === categoryName);
        if (!row) return null;
        return {
          ...row,
          category_attempt_status: idx < completedCount ? 'completed' : 'pending_attempt',
        };
      })
      .filter(Boolean);
    const nextIdx = tests.findIndex((t) => t.category_attempt_status !== 'completed');
    const nextName = nextIdx >= 0 ? tests[nextIdx].category_name : null;
    return {
      fullTestAttemptId: Number(attemptId),
      groupId: Number(groupId),
      isSpeakingOptional,
      tests,
      nextPendingCategoryName: nextName,
      nextPendingCategoryIndex: nextIdx >= 0 ? nextIdx : null,
    };
  };

  const buildGroupedTestsFromResumePayload = (payload) => {
    const sectionOrder = ['Listening', 'Reading', 'Writing', 'Speaking'];
    const pickCategoryRow = (categoryName) => {
      const rows = payload.tests.filter((t) => t.category_name === categoryName);
      if (rows.length === 0) return null;
      const completedRow = rows.find((t) => t.category_attempt_status === 'completed');
      if (completedRow) return completedRow;
      const pendingRow = rows.find((t) => t.category_attempt_status === 'pending_attempt');
      return pendingRow || rows[0];
    };
    return sectionOrder
      .map((categoryName) => pickCategoryRow(categoryName))
      .filter(Boolean)
      .map((t) => ({
        id: t.id,
        category_name: t.category_name,
        groupId: payload.groupId,
        group_name: t.group_name,
        test_type: t.test_type,
        audio_url: t.audio_url,
        sections: t.sections || [],
      }));
  };

  const resolveFullTestResumeAttemptId = (groupTest) => {
    const grouped = tests.filter((t) => t.groupId === groupTest.groupId);
    const resumeRow = grouped.find(
      (t) => t.has_in_progress_full_attempt || t.resume_full_test_attempt_id
    );
    const latestSummary = resumeRow?.latest_full_attempt_summary || null;
    return Number(
      resumeRow?.resume_full_test_attempt_id ||
        latestSummary?.full_test_attempt_id ||
        groupTest.resume_full_test_attempt_id ||
        groupTest.latest_full_attempt_summary?.full_test_attempt_id ||
        0
    );
  };

  const handleFullTestResume = async (groupTest) => {
    const attemptId = resolveFullTestResumeAttemptId(groupTest);
    if (!attemptId) {
      alert('No in-progress attempt to resume.');
      return;
    }
    const sectionOrderFull = ['Listening', 'Reading', 'Writing', 'Speaking'];
    const catalogSorted = tests
      .filter((t) => t.groupId === groupTest.groupId)
      .sort((a, b) => {
        const ai = sectionOrderFull.indexOf(a.category_name);
        const bi = sectionOrderFull.indexOf(b.category_name);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });

    const summary = groupTest.latest_full_attempt_summary;
    if (summary && catalogSorted.length > 0) {
      const prefetchedResumeContext = buildResumePrefetchFromTestsList(
        catalogSorted,
        summary,
        attemptId,
        groupTest.groupId
      );
      navigate('/ielts-full-test', {
        state: {
          mode: 'practice',
          groupedTests: catalogSorted,
          groupId: groupTest.groupId,
          continuePrevious: true,
          resumeFullTestAttemptId: Number(attemptId),
          prefetchedResumeContext,
        },
      });
      return;
    }

    const loadingKey = `full-${groupTest.groupId}-resume`;
    setLoadingTestId(loadingKey);
    try {
      const response = await fetch(
        `${Base_URL}/app/users/ielts-tests/full-attempts/${attemptId}/resume-context`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      const data = await response.json();
      const payload = data?.response;
      if (!response.ok || data?.responseCode !== 200 || !payload?.tests?.length) {
        throw new Error(data?.message || 'Unable to load full test for resume');
      }
      const groupedTests = buildGroupedTestsFromResumePayload(payload);
      navigate('/ielts-full-test', {
        state: {
          mode: 'practice',
          groupedTests,
          groupId: payload.groupId,
          continuePrevious: true,
          resumeFullTestAttemptId: Number(attemptId),
          prefetchedResumeContext: {
            ...payload,
            fullTestAttemptId: Number(attemptId),
          },
        },
      });
    } catch (e) {
      console.error(e);
      alert(e.message || 'Unable to resume full test');
    } finally {
      setLoadingTestId(null);
    }
  };

  const handleFullTestStart = async (groupTest) => {
    const shouldResume = Boolean(
      groupTest.has_in_progress_full_attempt ||
        groupTest.resume_full_test_attempt_id ||
        String(groupTest.latest_full_attempt_summary?.status || '').toLowerCase() === 'in_progress'
    );
    const loadingKey = `full-${groupTest.groupId}-start`;
    setLoadingTestId(loadingKey);
    try {
      const groupedTests = tests.filter((t) => t.groupId === groupTest.groupId);
      if (shouldResume) {
        const response = await fetch(`${Base_URL}/app/users/ielts-tests/full-attempts/start`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ groupId: groupTest.groupId }),
        });
        const data = await response.json();
        if (data.responseCode !== 200 || !data.response?.fullTestAttemptId) {
          throw new Error(data.message || 'Could not start a new attempt');
        }
        navigate('/ielts-full-test', {
          state: {
            mode: 'practice',
            groupedTests,
            groupId: groupTest.groupId,
            continuePrevious: false,
            fullTestAttemptId: data.response.fullTestAttemptId,
          },
        });
      } else {
        navigate('/ielts-full-test', {
          state: {
            mode: 'practice',
            groupedTests,
            groupId: groupTest.groupId,
          },
        });
      }
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to start full test');
    } finally {
      setLoadingTestId(null);
    }
  };

  const handleStartTest = async (testId, mode, loadingId = null) => {
    console.log(`Starting test ${testId} in ${mode || 'default'} mode`);
    const uniqueLoadingId = loadingId || `sectional-${testId}`;
    // Sectional tests (with or without groupId): fetch test details
    setLoadingTestId(uniqueLoadingId);
    try {
        const response = await fetch(
          `${Base_URL}/app/users/ielts-tests/getTestById/${testId}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        const test = data.response;
        
        if (!test) {
          throw new Error("Test not found");
        }
        
        if (test.category_name === 'Listening') {
          navigate('/ielts-listening-test', { 
            state: { 
              duration: 30, // Default duration for listening test (IELTS Listening is 30 min)
              mode: mode || 'practice',
              testData: test // Pass the full test object with sections
            } 
          });
        } else if (test.category_name === 'Writing') {
          navigate('/ielts-writing-test', { 
            state: { 
              duration: 60, // Default duration for writing test
              mode: mode || 'practice',
              testData: test // Pass the full test object with sections
            } 
          });
        } else if (test.category_name === 'Reading') {
          navigate('/ielts-reading-test', { 
            state: { 
              duration: 60, // Default duration for reading test
              mode: mode || 'practice',
              testData: test // Pass the full test object with sections
            } 
          });
        } else if (test.category_name === 'Speaking') {
          const userData = JSON.parse(localStorage.getItem('userData') || '{}');
          const currentUserId = userData?.UserId || 1;
          navigate('/ielts-speaking-test', {
            state: {
              duration: 15, // Default duration for speaking test
              mode: mode || 'practice',
              testData: test, // Pass the full test object with sections
              currentUserId
            }
          });
        }
      } catch (error) {
        console.error("Error fetching test details:", error);
        alert("Failed to load test details. Please try again.");
      } finally {
        setLoadingTestId(null);
      }
  };

  const handleViewAllFullTests = () => {
    console.log("View all full tests");
  };

  const handleViewAllSectionalTests = () => {
    console.log("View all sectional tests");
  };

  // Map dropdown value to test_kind for filtering
  const kindMap = {
    "Cambridge": "cambridge",
    "British Council": "british council",
    "IDP": "idp"
  };

  // Group tests by groupId for Full Test section
  const getFullTestGroups = () => {
    const groupedTests = {};
    tests.forEach(test => {
      if (test.groupId) {
        if (!groupedTests[test.groupId]) {
          groupedTests[test.groupId] = [];
        }
        groupedTests[test.groupId].push(test);
      }
    });

    // Convert to array and filter by selected kind and test type
    // Preserve backend order by using the order tests appear in the original array
    const groupOrder = new Map(); // Track order of first appearance of each group
    tests.forEach((test, index) => {
      if (test.groupId && !groupOrder.has(test.groupId)) {
        groupOrder.set(test.groupId, index);
      }
    });

    const fullTestGroups = Object.entries(groupedTests)
      .map(([groupId, groupTests]) => {
        // Check if any test in the group matches the selected kind AND test type
        // Always check both kind and type, even when "All" is selected for kind
        const hasMatchingFilters = groupTests.some(test => {
          // Match by test_kind (Cambridge, British Council, IDP)
          const matchesKind = selectedKind === 'All' ? true : (test.test_kind ? test.test_kind.toLowerCase() === (kindMap[selectedKind] || selectedKind.toLowerCase()) : false);
          // Match by test type (academic/general) - always check this
          const matchesType = test.test_type ? test.test_type.toLowerCase() === selectedTestType : true;
          return matchesKind && matchesType;
        });
        
        if (hasMatchingFilters) {
          // Create a representative test object for the group
          const representativeTest = groupTests[0]; // Use first test as representative
          const allCategories = [...new Set(groupTests.map(t => t.category_name))].filter(Boolean);
          const totalDuration = groupTests.reduce((sum, test) => sum + (test.total_time || 0), 0);
          
          // Check if all required categories are present
          const requiredCategories = ['Listening', 'Reading', 'Writing'];
          const categoryNamesLower = allCategories.map(cat => cat.toLowerCase());
          const hasAllRequiredCategories = requiredCategories.every(cat => 
            categoryNamesLower.includes(cat.toLowerCase())
          );
          const missingCategories = requiredCategories.filter(cat => 
            !categoryNamesLower.includes(cat.toLowerCase())
          );
          
          // Get group_name from any test in the group (they should all have the same group_name)
          const groupName = representativeTest.group_name || groupTests.find(t => t.group_name)?.group_name;
          
          // Format test name: use group_name if available, otherwise use default format
          let testName = `Complete IELTS Test ${representativeTest.groupId}`;
          if (groupName) {
            testName = groupName;
          }
          
          return {
            id: representativeTest.id,
            groupId: representativeTest.groupId,
            test_name: testName,
            group_name: groupName,
            test_type: representativeTest.test_type,
            test_kind: representativeTest.test_kind || 'Cambridge',
            total_time: totalDuration > 0 ? totalDuration : 165, // Default 2h 45m (30+60+60+15)
            categories: allCategories,
            testsInGroup: groupTests.length,
            groupTests: groupTests,
            has_in_progress_full_attempt: groupTests.some(
              (t) => t.has_in_progress_full_attempt || t.resume_full_test_attempt_id
            ),
            resume_full_test_attempt_id:
              groupTests.find((t) => t.resume_full_test_attempt_id)?.resume_full_test_attempt_id || null,
            latest_full_attempt_summary:
              groupTests.find((t) => t.latest_full_attempt_summary)?.latest_full_attempt_summary || null,
            hasAllRequiredCategories,
            missingCategories,
            _order: groupOrder.get(parseInt(groupId)) || 0
          };
        }
        return null;
      })
      .filter(Boolean)
      // Sort by original backend order (preserve order from tests array)
      .sort((a, b) => a._order - b._order);

    return fullTestGroups;
  };

  const fullTests = getFullTestGroups();
  
  // Get user subscription status
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const isSubscribed = userData?.IsSubscribed || false;
  
  // Add isUnlocked property to full tests (only first test unlocked if not subscribed)
  const fullTestsWithLock = fullTests.map((test, index) => ({
    ...test,
    isUnlocked: isSubscribed ? true : index === 0
  }));

  // Pagination for Full Tests
  const fullTestsStartIndex = (fullTestPage - 1) * fullTestsPerPage;
  const paginatedFullTests = fullTestsWithLock.slice(fullTestsStartIndex, fullTestsStartIndex + fullTestsPerPage);
  const totalFullTestPages = Math.ceil(fullTestsWithLock.length / fullTestsPerPage);

  // Filter tests by selectedSection for Sectional Test section
  // Include ALL tests (with or without groupId) that match the selected section
  // Tests with groupId are part of a full test but can also be attempted individually
  // Note: Tests are already sorted by backend (by root test's created_at), so we preserve that order
  const sectionalTests = tests.filter(test => {
    // Match by category name (Listening, Reading, Writing, Speaking)
    const matchesSection = test.category_name && test.category_name.toLowerCase() === selectedSection.toLowerCase();
    // Match by test type (academic/general) - default to true if test_type is not set
    const matchesType = test.test_type ? test.test_type.toLowerCase() === selectedTestType : true;
    // Match by test_kind (Cambridge, British Council, IDP) - if "All" is selected, match all kinds
    const matchesKind = selectedKind === 'All' ? true : (test.test_kind ? test.test_kind.toLowerCase() === (kindMap[selectedKind] || selectedKind.toLowerCase()) : false);
    // Return true if all conditions match (tests with groupId are included)
    return matchesSection && matchesType && matchesKind;
  });

  // Add isUnlocked property to sectional tests (only first test unlocked per section if not subscribed)
  const sectionalTestsWithLock = sectionalTests.map((test, index) => ({
    ...test,
    isUnlocked: isSubscribed ? true : index === 0
  }));
  
  // Pagination for Sectional Tests
  const sectionalTestsStartIndex = (sectionalTestPage - 1) * sectionalTestsPerPage;
  const paginatedSectionalTests = sectionalTestsWithLock.slice(sectionalTestsStartIndex, sectionalTestsStartIndex + sectionalTestsPerPage);
  const totalSectionalTestPages = Math.ceil(sectionalTestsWithLock.length / sectionalTestsPerPage);

  // Dropdown change handler
  const handleFilterChange = (value) => {
    setSelectedKind(value);
  };

  // Section tab click handler
  const handleSectionTabClick = (sectionTitle) => {
    setSelectedSection(sectionTitle);
    // Removed navigation for Reading and Writing to show tests here
  };

  // Helper function to get the appropriate icon for a section
  // Note: Color is now handled by SectionCard component
  const getSectionIcon = (iconName) => {
    switch (iconName) {
      case 'listening':
        return <ListeningIcon sx={{ fontSize: 24 }} />;
      case 'reading':
        return <ReadingIcon sx={{ fontSize: 24 }} />;
      case 'writing':
        return <WritingIcon sx={{ fontSize: 24 }} />;
      case 'speaking':
        return <SpeakingIcon sx={{ fontSize: 24 }} />;
      default:
        return null;
    }
  };

  // Render the Full Test section
  const renderFullTestSection = () => (
    <Box sx={{ width: '100%' }}>
      <TestSection 
        title="Full Test" 
        filter={{
          options: testFilters,
          defaultValue: testFilters[0].value,
          value: selectedKind,
          onChange: (e) => handleFilterChange(e.target ? e.target.value : e),
          sx: selectStyles
        }}
        onViewAll={handleViewAllFullTests}
      >
        <TestListContainer>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <CircularProgress size={30} sx={{ color: themeColors.primary }} />
            </Box>
          ) : error ? (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <Typography color="error">{error}</Typography>
            </Box>
          ) : paginatedFullTests.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <Typography color="textSecondary">No tests available.</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ flex: 1 }}>
                {paginatedFullTests.map(test => {
                  const isMissingCategories = !test.hasAllRequiredCategories;
                  const disabledMessage = isMissingCategories 
                    ? `Full test is under development. ${test.missingCategories?.join(', ')} sections are being added.`
                    : null;
                  const shouldResume = Boolean(
                    test.has_in_progress_full_attempt ||
                    test.resume_full_test_attempt_id ||
                    String(test.latest_full_attempt_summary?.status || '').toLowerCase() === 'in_progress'
                  );
                  
                  return (
                    <TestCard 
                      key={`group-${test.groupId}`}
                      title={test.test_name}
                      time={test.total_time ? `${Math.floor(test.total_time / 60)}h:${String(test.total_time % 60).padStart(2, '0')}m` : '2h:30m'}
                      difficulty={test.test_type || 'N/A'}
                      onStart={() => handleFullTestStart(test)}
                      onResume={() => handleFullTestResume(test)}
                      showResumeButton={shouldResume}
                      startLabel="Start"
                      loadingStart={loadingTestId === `full-${test.groupId}-start`}
                      loadingResume={loadingTestId === `full-${test.groupId}-resume`}
                      isLocked={!test.isUnlocked}
                      disabledMessage={disabledMessage}
                    />
                  );
                })}
              </Box>
              {totalFullTestPages > 1 && (
                <Box display="flex" justifyContent="center" mt="auto" pt={3} mb={1}>
                  <Pagination 
                    count={totalFullTestPages} 
                    page={fullTestPage} 
                    onChange={(e, page) => setFullTestPage(page)}
                    color="primary"
                    size="medium"
                    shape="rounded"
                    siblingCount={1}
                    boundaryCount={1}
                    sx={paginationStyles}
                  />
                </Box>
              )}
            </>
          )}
        </TestListContainer>
      </TestSection>
    </Box>
  );

  // Render the Sectional Test section
  const renderSectionalTestSection = () => (
    <Box sx={{ width: '100%' }}>
      <TestSection 
        title="Sectional Test" 
        onViewAll={handleViewAllSectionalTests}
      >
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {sections.map(section => (
            <Grid item xs={6} sm={3} key={section.id}>
              <SectionCard 
                title={section.title}
                count={sectionCounts[section.title] || 0}
                icon={getSectionIcon(section.icon)}
                onClick={() => handleSectionTabClick(section.title)}
                isSelected={selectedSection === section.title}
              />
            </Grid>
          ))}
        </Grid>
        <TestListContainer>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <CircularProgress size={30} sx={{ color: themeColors.primary }} />
            </Box>
          ) : error ? (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <Typography color="error">{error}</Typography>
            </Box>
          ) : paginatedSectionalTests.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <Typography color="textSecondary">No sectional tests available.</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ flex: 1 }}>
                {paginatedSectionalTests.map(test => {
                  const testName = test.test_name || `Test ${test.id}`;
                  return (
                    <SectionalTestCard 
                      key={test.id}
                      title={testName}
                      time={test.total_time ? `${Math.floor(test.total_time / 60)}h:${String(test.total_time % 60).padStart(2, '0')}m` : (() => {
                        const testNameLower = testName.toLowerCase();
                        if (testNameLower.includes('reading')) return '01:00h';
                        if (testNameLower.includes('writing')) return '01:00h';
                        if (testNameLower.includes('speaking')) return '00:15m';
                        if (testNameLower.includes('listening')) return '00:30m';
                        return '00:30m';
                      })()}
                      difficulty={test.test_type || 'N/A'}
                      onStart={(mode) => {
                        // For sectional tests, use unique identifier
                        const loadingId = `sectional-${test.id}`;
                        handleStartTest(test.id, mode, loadingId);
                      }}
                      testType={testName.split('-').pop().trim()}
                      selectedSection={selectedSection}
                      isLocked={!test.isUnlocked}
                      loading={loadingTestId === `sectional-${test.id}`}
                    />
                  );
                })}
              </Box>
              {totalSectionalTestPages > 1 && (
                <Box display="flex" justifyContent="center" mt="auto" pt={3} mb={1}>
                  <Pagination 
                    count={totalSectionalTestPages} 
                    page={sectionalTestPage} 
                    onChange={(e, page) => setSectionalTestPage(page)}
                    color="primary"
                    size="medium"
                    shape="rounded"
                    siblingCount={1}
                    boundaryCount={1}
                    sx={paginationStyles}
                  />
                </Box>
              )}
            </>
          )}
        </TestListContainer>
      </TestSection>
    </Box>
  );

  // Render target score modal
  const renderTargetScoreModal = () => (
    <Dialog
      open={showTargetScoreModal}
      onClose={() => setShowTargetScoreModal(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          padding: '8px'
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        color: themeColors.primary, 
        fontWeight: 600,
        fontSize: '24px',
        pb: 1
      }}>
        Update Target Score
      </DialogTitle>
      
      <DialogContent sx={{ pb: 1 }}>
        <Typography variant="body1" color="text.secondary" paragraph sx={{ textAlign: 'center', mb: 3 }}>
          Set your IELTS target score (0.0 - 9.0)
        </Typography>
        
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Target Score</InputLabel>
          <Select
            value={newTargetScore}
            label="Target Score"
            onChange={(e) => setNewTargetScore(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              }
            }}
          >
            {[4.0, 4.5, 5.0, 5.5, 6.0, 6.5, 7.0, 7.5, 8.0, 8.5, 9.0].map((score) => (
              <MenuItem key={score} value={score}>
                {score.toFixed(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 2 }}>
        <Button
          onClick={updateTargetScore}
          variant="contained"
          disabled={!newTargetScore || isUpdating}
          sx={{ 
            backgroundColor: themeColors.primary,
            '&:hover': { backgroundColor: themeColors.primaryHover },
            px: 3
          }}
        >
          {isUpdating ? <CircularProgress size={20} color="inherit" /> : 'Update'}
        </Button>
        <Button
          onClick={() => {
            setShowTargetScoreModal(false);
            setNewTargetScore('');
          }}
          variant="outlined"
          disabled={isUpdating}
          sx={{ 
            borderColor: themeColors.primary,
            color: themeColors.primary,
            '&:hover': { 
              borderColor: themeColors.primaryHover,
              backgroundColor: themeColors.primaryLighter
            },
            px: 3
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Render exam date modal
  const renderExamDateModal = () => (
    <Dialog
      open={showExamDateModal}
      onClose={() => setShowExamDateModal(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          padding: '8px'
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        color: themeColors.primary, 
        fontWeight: 600,
        fontSize: '24px',
        pb: 1
      }}>
        Update Exam Date
      </DialogTitle>
      
      <DialogContent sx={{ pb: 1 }}>
        <Typography variant="body1" color="text.secondary" paragraph sx={{ textAlign: 'center', mb: 3 }}>
          Set your IELTS exam date
        </Typography>
        
        <TextField
          label="Exam Date"
          type="date"
          value={newExamDate}
          onChange={(e) => setNewExamDate(e.target.value)}
          fullWidth
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            min: new Date().toISOString().split('T')[0], // Today's date as minimum
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
            }
          }}
        />
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 2 }}>
        <Button
          onClick={updateExamDate}
          variant="contained"
          disabled={!newExamDate || isUpdating}
          sx={{ 
            backgroundColor: themeColors.primary,
            '&:hover': { backgroundColor: themeColors.primaryHover },
            px: 3
          }}
        >
          {isUpdating ? <CircularProgress size={20} color="inherit" /> : 'Update'}
        </Button>
        <Button
          onClick={() => {
            setShowExamDateModal(false);
            setNewExamDate('');
          }}
          variant="outlined"
          disabled={isUpdating}
          sx={{ 
            borderColor: themeColors.primary,
            color: themeColors.primary,
            '&:hover': { 
              borderColor: themeColors.primaryHover,
              backgroundColor: themeColors.primaryLighter
            },
            px: 3
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <div className="dashboard-root-flex">
      <BrowserNotSupported open={open} close={setOpen} />
      <Navbar />
      <div className="dashboard-content-flex">
      <DashboardContainer maxWidth="lg">
        {/* Score Cards */}
        <Grid container spacing={4} sx={{ mt: { xs: 2, md: 3 }, mb: { xs: 2, md: 4 } }}>
          <Grid item xs={12} sm={4}>
            <ScoreCard 
              title="Average Score" 
              score={profileData.loading ? "Loading..." : profileData.error ? "Error" : profileData.averageScore} 
              icon={<CalculatorIcon sx={{ fontSize: 28, color: '#FF6B6B' }} />}
              bgcolor="#FFEAEA"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ScoreCard 
              title="Target Score" 
              score={profileData.loading ? "Loading..." : profileData.error ? "Error" : profileData.targetScore} 
              icon={<TargetIcon sx={{ fontSize: 28, color: themeColors.primary }} />}
              bgcolor={themeColors.primaryLighter}
              showChangeButton
              onChangeClick={handleChangeTargetScore}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ScoreCard 
              title="Exam In" 
              score={profileData.loading ? "Loading..." : profileData.error ? "Error" : profileData.examIn} 
              icon={<CalendarIcon sx={{ fontSize: 28, color: '#00B4D8' }} />}
              bgcolor="#E1F9FF"
              showChangeButton
              onChangeClick={handleChangeExamDate}
            />
          </Grid>
        </Grid>

        {/* Test Sections - Side by side on larger screens, stacked on mobile */}
        {isMobile ? (
          <Box sx={{ mt: 3 }}>
            {renderFullTestSection()}
            <Box sx={{ height: '32px' }} />
            {renderSectionalTestSection()}
          </Box>
        ) : (
          <Grid container spacing={4} sx={{ mt: 4, mb: 4 }}>
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              {renderFullTestSection()}
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              {renderSectionalTestSection()}
            </Grid>
          </Grid>
        )}
      </DashboardContainer>
      </div>
      {showFooter && (
        <Box sx={{ padding: "0rem 3% 0rem", background: "white" }}>
          <Footer />
        </Box>
      )}
      {renderTargetScoreModal()}
      {renderExamDateModal()}
    </div>
  );
};

export default IELTSDashboard; 